// how-to-start/utils/apply-brand-logo.ts
//
// THE USER'S LOGO, NOT THE TEMPLATE'S PLACEHOLDER.
//
// Every template ships with a stand-in mark -- "Logoipsum" -- and it survived
// into the finished document: a deck generated for a real company opened on
// somebody's placeholder logo. The brand is already known by then; the funnel
// resolves it from the account's Story Brain and attaches it to the story as
// `settings.brandImport`, logos and all. Nothing was reading it.
//
// A Logo ELEMENT is a placeholder by definition -- it exists so a brand can be
// dropped into it -- so every one of them is replaced when there is a brand to
// replace it with. Where there is no brand, or no usable logo file, the
// template's own mark stays: a broken image is worse than a placeholder.
//
// The variant follows the BACKGROUND EACH MARK SITS ON, slot by slot.
// Brandfetch returns light and dark marks, and picking blindly is what once
// put a white logo on a white slide. Deciding once per slide is the same
// mistake one step up: a slide with a mark in a white header strip and
// another on a dark band below it has two answers, and taking the first
// leaves the other one invisible.

import isDarkColor from "@providers/custom-dnd-provider/hooks/use-invert-color/utils/is-dark-color";
import {
  pickBrandLogoUrl,
  resolveCssColor,
} from "@editor/modules/header/ai/pipelines/inject-brand-logo";
import type { BrandLogoSnapshot } from "@editor/modules/header/ai/pipelines/inject-brand-logo";
import { isLogoSlotImage } from "@editor/modules/header/ai/pipelines/logo-slot";

type Node = {
  type?: string;
  settings?: Record<string, unknown> & { name?: string; src?: unknown };
  children?: unknown;
};

const asNodes = (children: unknown): Node[] =>
  Array.isArray(children) ? (children as Node[]) : [];

/** Whether a variable drives this element's picture (any value counts). */
const hasVariableSrc = (settings: Node["settings"]): boolean =>
  typeof settings?.dynamicVariableSrc === "string" &&
  settings.dynamicVariableSrc.trim().length > 0;

/**
 * `slides` with every logo placeholder replaced by the brand's own mark.
 *
 * Mutates nothing: returns new slides, and returns them unchanged when there
 * is no brand or nothing usable in it.
 */
export const applyBrandLogo = <T>(
  slides: T[],
  brand: BrandLogoSnapshot | null | undefined,
  designSystem?: Record<string, unknown> | null,
): { slides: T[]; replaced: number } => {
  if (!Array.isArray(slides) || !brand?.logos?.length) {
    return { slides, replaced: 0 };
  }

  // The two marks, resolved once. Each falls back to the other theme's rather
  // than leaving a placeholder: a logo in the wrong theme is still the right
  // company. Both null means the brand has no usable logo file at all.
  const onDark =
    pickBrandLogoUrl(brand, true) ?? pickBrandLogoUrl(brand, false);
  const onLight =
    pickBrandLogoUrl(brand, false) ?? pickBrandLogoUrl(brand, true);
  if (!onDark && !onLight) return { slides, replaced: 0 };

  /** A Logo element, whatever case the template spelled its name in. */
  const isLogoElement = (n: Node) =>
    typeof n.settings?.name === "string" &&
    n.settings.name.toLowerCase() === "logo";

  let replaced = 0;
  const next = slides.map((slide) => {
    const visit = (
      node: unknown,
      siblings: Node[],
      // WHAT THIS MARK IS ACTUALLY SITTING ON, not what the slide's root says.
      //
      // A logo is usually nested inside a band or a card whose colour differs
      // from the slide behind it, and that colour is normally a theme variable
      // -- `var(--theme-color-3)` is what a template paints its dark slides
      // with. The nearest resolvable ancestor background wins; no resolvable
      // background anywhere up the chain reads as light, so a white mark is
      // never painted on a white slide.
      ancestorBg: string | null,
    ): unknown => {
      if (!node || typeof node !== "object") return node;
      const n = node as Node;
      const bg =
        resolveCssColor(n.settings?.backgroundColor, designSystem) ??
        ancestorBg;
      const own = asNodes(n.children);
      const children = own.map((child) => visit(child, own, bg));
      const url = bg && isDarkColor(bg) ? onDark : onLight;
      // A mark a VARIABLE drives is the variable's: each version puts its
      // own there, and the brand's would overwrite the binding for all of
      // them. The same setting the editor reads (dynamicVariableSrc).
      if (hasVariableSrc(n.settings)) {
        return Array.isArray(n.children) ? { ...n, children } : n;
      }
      if (url && isLogoElement(n)) {
        replaced++;
        return { ...n, settings: { ...n.settings, src: url }, children };
      }
      // The mark carried as a small Image beside the heading -- a saved
      // slide's cover, most often. Contained, not cropped. See logo-slot.
      if (
        url &&
        n.type === "element" &&
        isLogoSlotImage(n.settings, siblings)
      ) {
        replaced++;
        return {
          ...n,
          settings: { ...n.settings, src: url, shape: "free" },
          children,
        };
      }
      return Array.isArray(n.children) ? { ...n, children } : n;
    };

    return visit(slide, [], null) as T;
  });

  return { slides: next, replaced };
};
